import { memo, useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import { Link } from '~/components/Link'
import { TopicData } from '~/types/theory'
import { Breakpoint, useCheckBreakpoint } from '~/utils'

import BreadcrumbSchema from '~/components/Metadata/Schema/BreadcrumbSchema'

function BreadCrumbs({
  breadcrumbs,
  isWhite = false,
  itemClassNames = 'inline-flex items-center',
  wrapperClassNames = '',
}: {
  breadcrumbs: TopicData[]
  isWhite?: boolean
  itemClassNames?: string
  wrapperClassNames?: string
}): React.ReactElement {
  const router = useRouter()

  const [expandAll, setExpandAll] = useState(false)
  useEffect(() => {
    router.events.on('routeChangeComplete', () => setExpandAll(false))
    return () => router.events.off('routeChangeComplete', () => setExpandAll(false))
  }, [router])

  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })

  const maxLevel = breadcrumbs.length - 1
  if (maxLevel === 0) return <></>

  const shouldExpandButton = !expandAll && maxLevel > 1 && isMobile
  const filteredBreadcrumbs = shouldExpandButton && isMobile ? breadcrumbs.slice(-2) : breadcrumbs

  return (
    <div className={wrapperClassNames}>
      <BreadcrumbSchema breadcrumbs={breadcrumbs} />

      {shouldExpandButton && (
        <div
          className="inline-flex items-center w-3 h-4 relative leading-9 md:leading-6 cursor-pointer"
          onClick={() => setExpandAll(true)}
          role="button"
        >
          <span className="absolute text-white">...</span>
        </div>
      )}

      {filteredBreadcrumbs.map((crumb, index) => (
        <BreadCrumbsItem
          crumb={crumb}
          isWhite={isWhite}
          itemClassNames={itemClassNames}
          key={crumb.path}
          level={shouldExpandButton ? 1 : index}
        />
      ))}
    </div>
  )
}

function BreadCrumbsItem({
  level,
  crumb,
  isWhite,
  itemClassNames,
}: {
  crumb: TopicData
  level: number
  isWhite: boolean
  itemClassNames: string
}): React.ReactElement {
  const prefix = level > 0 ? '>' : ''
  const textColor = isWhite ? 'text-white' : 'text-gray-sub-text'

  return (
    <span className={`${itemClassNames} text-sm ${textColor} leading-9 md:leading-6`} key={crumb.path}>
      {prefix && <span className="inline-block mx-2 md:mx-0.5">{prefix}</span>}
      <Link className="my-1 md:my-0 p-1 md:p-0 leading-normal" to={crumb.path}>
        {crumb.h1}
      </Link>
    </span>
  )
}

export default memo(BreadCrumbs)
